import type { HTMLAttributes, ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { SectionHeading } from './text';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

/**
 * The page's main unit: a hairline above, a heading, content. Sections are
 * separated by rules, not boxed — the timetable reads top to bottom.
 */
export function Section({
  title,
  action,
  children,
  divided = true,
  className,
  ...rest
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  divided?: boolean;
} & HTMLAttributes<HTMLElement>) {
  return (
    <section className={cx('section', divided && 'section-divided', className)} {...rest}>
      {title && <SectionHeading action={action}>{title}</SectionHeading>}
      {children}
    </section>
  );
}

/** A bounded surface for grouped settings, budget tiles and desktop panels. Never nested. */
export function Card({
  children,
  flush,
  className,
  as: Tag = 'div',
  ...rest
}: { children: ReactNode; flush?: boolean; as?: 'div' | 'section' | 'article' } & HTMLAttributes<HTMLElement>) {
  return (
    <Tag className={cx('card', flush && 'card-flush', className)} {...rest}>
      {children}
    </Tag>
  );
}

export function List({ children, divided = true, inset, label }: { children: ReactNode; divided?: boolean; inset?: boolean; label?: string }) {
  return (
    <ul className={cx('list', divided && 'list-divided', inset && 'list-inset')} aria-label={label}>
      {children}
    </ul>
  );
}

export interface ListRowProps {
  leading?: ReactNode;
  title: ReactNode;
  /** Sits right after the title, e.g. a change marker. */
  titleAddon?: ReactNode;
  subtitle?: ReactNode;
  /** Muted column before the value, e.g. a due date. */
  meta?: ReactNode;
  trailing?: ReactNode;
  density?: 'compact' | 'regular';
  to?: string;
  onClick?: () => void;
  /** Show a chevron on interactive rows (default: when `to` or `onClick`). */
  chevron?: boolean;
}

export function ListRow({ leading, title, titleAddon, subtitle, meta, trailing, density = 'regular', to, onClick, chevron }: ListRowProps) {
  const interactive = Boolean(to || onClick);
  const showChevron = chevron ?? interactive;
  const body = (
    <>
      {leading && <span className="row-leading">{leading}</span>}
      <span className="row-main">
        <span className="row-title">
          <span className="row-title-text">{title}</span>
          {titleAddon}
        </span>
        {subtitle && <span className="row-subtitle">{subtitle}</span>}
      </span>
      {meta !== undefined && <span className="row-meta num">{meta}</span>}
      {trailing !== undefined && <span className="row-trailing num">{trailing}</span>}
      {showChevron && <ChevronRight aria-hidden size={18} className="row-chevron" />}
    </>
  );
  const className = cx('row', `row-${density}`, interactive && 'row-interactive');
  return (
    <li className="list-item">
      {to ? (
        <Link to={to} className={className}>
          {body}
        </Link>
      ) : onClick ? (
        <button type="button" className={className} onClick={onClick}>
          {body}
        </button>
      ) : (
        <div className={className}>{body}</div>
      )}
    </li>
  );
}

export function Divider() {
  return <hr className="divider" />;
}
